import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'lib/utils';

type ViewOnlyBadgeProps = {
  title?: string;
  description?: string;
  className?: string;
};

/**
 * Yellow warning callout for read-only / non-owner views.
 *
 * @example
 * {!canEdit ? <ViewOnlyBadge /> : null}
 * <ViewOnlyBadge description="You cannot edit this tool." />
 */
export function ViewOnlyBadge({
  title,
  description,
  className,
}: ViewOnlyBadgeProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'w-full shrink-0 rounded-lg border border-amber-400 bg-amber-50 p-3 transition-all sm:max-w-[280px] dark:border-amber-500/50 dark:bg-amber-950/30',
        className
      )}
      role="status"
    >
      <div className="flex items-center gap-1">
        <TriangleAlert
          className="size-4 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden
        />
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
          {title ?? (t('VIEW_ONLY') || 'View only.')}
        </p>
      </div>
      <p className="mt-0.5 text-xs font-normal text-amber-700 dark:text-amber-400/90">
        {description ??
          (t('YOU_ARE_NOT_THE_OWNER_OF_THIS_RESOURCE') ||
            'You are not the owner of this resource.')}
      </p>
    </div>
  );
}

export type { ViewOnlyBadgeProps };
